import { FunctionComponent } from 'react';
import RSCard from '../../../ui/containers/RSCard';
import { useMarketsSnapshot } from '../../../../api/rs-hg/markets-snapshot/hooks/queries';
import { setWeekDayDate } from '../../markets/MarketsOverview';
import { getVarExceedanceClass } from '../dashboard-utils';
import SimpleLineChart from '../../markets/subComponents/AssetsCharts';
import TableSkeletonLoader from '../../../ui/TableSkeletonLoader';
import { MarketData } from '../../../../api/rs-hg/markets-snapshot/types';

const MarketsSnapshot: FunctionComponent = () => {
  const { data, isPending, error } = useMarketsSnapshot(setWeekDayDate(1), {
    staleTime: 1000 * 60 * 5,
  });

  return (
    <RSCard key="markets-section" className="flex h-full w-full flex-col overflow-hidden">
      <div className="border-b border-gray-200 pb-2">
        <div className="flex w-full flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-mainblue">
            Markets Snapshot
          </h1>
        </div>
      </div>
      <div className="mt-4 flex min-h-0 flex-1 flex-col">
        <article className="flex min-h-0 flex-1 flex-col">
          {isPending && <TableSkeletonLoader />}
          {error && (
            <ErrorFetchMessage message="Error Retrieving Market Data" />
          )}

          {!error && !isPending && (
            <MarketsMiniGraph data={data} />
          )}
        </article>
      </div>
    </RSCard>
  );
};

export default MarketsSnapshot;

interface MarketsMiniGraphProps {
  data: MarketData[];
}

const MarketsMiniGraph: FunctionComponent<MarketsMiniGraphProps> = ({
  data,
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full rounded-lg border border-gray-200 bg-white text-sm shadow-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-2 w-1/4 py-1 text-left text-gray-700">
              Asset Name
            </th>
            <th className="px-2 w-1/4 py-1 text-left text-gray-700">
              Asset Type
            </th>
            <th className="px-2 w-1/4 py-1 text-left text-gray-700">
              1D VAR Rati
            </th>
            <th className="px-2 py-1 w-1/4 text-left text-gray-700">
              Mini Time Series
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={index} className="border-t border-gray-200">
              <td className="w-1/4 px-2 py-1 text-gray-900">
                {item.asset_name}
              </td>
              <td className="w-1/6 px-2 py-1 text-gray-600">
                {item.asset_type}
              </td>
              <td
                className={`w-1/6 px-2 py-1 font-semibold text-gray-800 ${getVarExceedanceClass(item.var_ratio_1d)}`}
              >
                {item.var_ratio_1d.toFixed(4)}
              </td>
              <td className="w-1/2 px-2 py-1">
                <TimeSeriesSimple
                  timeSeriesData={item.mini_timeseries}
                  return1MValue={item.return_1m}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface TimeSeriesSimpleProps {
  timeSeriesData: [string, number][];
  return1MValue: number;
}

const TimeSeriesSimple: FunctionComponent<TimeSeriesSimpleProps> = ({
  timeSeriesData,
  return1MValue,
}) => {
  const dataPoints = Array.isArray(timeSeriesData)
    ? (timeSeriesData as [string, number][]).map(([, value]) => value)
    : [];

  return (
    <SimpleLineChart dataPoints={dataPoints} returnDay={return1MValue || 0} />
  );
};

interface ErrorFetchMessageProps {
  message: string;
}

const ErrorFetchMessage: FunctionComponent<ErrorFetchMessageProps> = ({
  message,
}) => {
  return (
    <div
      className="relative rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700"
      role="alert"
    >
      <strong className="font-bold">Failed Data Fetch: </strong>
      <span className="block sm:inline">{message}</span>
      <span className="absolute bottom-0 right-0 top-0 px-4 py-3">
        <svg
          className="h-6 w-6 fill-current text-red-500"
          role="button"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
        >
          <title>Close</title>
          <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
        </svg>
      </span>
    </div>
  );
};
